/**
 * The 3 · 2 · 1 that plays over the board while the game is `matched`.
 *
 * One `setState` per SECOND (three in a game's lifetime), never per frame: the
 * scale is an `Animated.Value` driven by the animation, not by React.
 */
import {useEffect, useState} from 'react';

import {useLiveGame} from '@/Store/liveGameStore';

/** PlayChess shows "Game to start in Ns" for 3 s after game_ready_response. */
const COUNTDOWN_MS = 3000;
const TICK_MS = 200;

const remaining = (readyAt: number): number =>
  Math.ceil((readyAt + COUNTDOWN_MS - Date.now()) / 1000);

export type BoardCountdownModel = {
  /** 3, 2, 1 — or 0 while there is nothing to count */
  digit: number;
};

export function useBoardCountdownModel(): BoardCountdownModel {
  const phase = useLiveGame(s => s.game.phase);
  const readyAt = useLiveGame(s => s.game.readyAt);
  const [digit, setDigit] = useState(0);

  const active = phase === 'starting' && !!readyAt;

  useEffect(() => {
    if (!active || !readyAt) {
      setDigit(0);
      return undefined;
    }
    const tick = () => setDigit(Math.min(3, Math.max(0, remaining(readyAt))));
    tick();
    const interval = setInterval(tick, TICK_MS);
    return () => clearInterval(interval);
  }, [active, readyAt]);

  return {digit: active ? digit : 0};
}
